// services/student.service.ts

import { axiosInstance } from "@/lib/axiosInstance";
import { AsyncSelectFetchParams, AsyncSelectFetchResult } from "@/components/form/types";

export type StudentStatus = "active" | "inactive";
export type PlacementStatus = "placed" | "pending" | "n/a";
export type StudentYear = "1st Year" | "2nd Year" | "3rd Year" | "4th Year";
export type StudentGender = "Male" | "Female" | "Other";
export type StudentSemester =
  | "1st"
  | "2nd"
  | "3rd"
  | "4th"
  | "5th"
  | "6th"
  | "7th"
  | "8th";

export interface StudentType {
  _id: string;
  rollNo: string; // e.g. "CSE2021001"
  name: string;
  gender: StudentGender;
  email: string;
  phone: string;
  dob: string; // ISO date string
  dept: string; // e.g. "1" (Dept ID)
  batch: string; // e.g. "1" (Batch ID)
  year: StudentYear;
  semester: StudentSemester;
  cgpa: number;
  status: StudentStatus;
  address: string;
  guardianName: string;
  guardianPhone: string;
  internship: string; // company name or "N/A"
  placement: PlacementStatus;
  createdAt: string;
  updatedAt: string;
}

export interface StudentCreateType {
  rollNo: string;
  name: string;
  gender: StudentGender;
  email: string;
  phone: string;
  dob: string;
  dept: string;
  batch: string;
  year: StudentYear;
  semester: StudentSemester;
  cgpa: number;
  status: StudentStatus;
  address: string;
  guardianName: string;
  guardianPhone: string;
  internship: string;
  placement: PlacementStatus;
}

export interface StudentMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type StudentParamsType = {
  search?: string;
  status?: StudentStatus | "all";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_STUDENTS: StudentType[] = [
  {
    _id: "1",
    rollNo: "CSE2021001",
    name: "Alex Johnson",
    gender: "Male",
    email: "alex.j@student.college.edu",
    phone: "+1-555-0101",
    dob: "2003-05-14",
    dept: "1",
    batch: "1",
    year: "4th Year",
    semester: "7th",
    cgpa: 8.9,
    status: "active",
    address: "123 College St, Apt 4B",
    guardianName: "Michael Johnson",
    guardianPhone: "+1-555-0102",
    internship: "Google",
    placement: "pending",
    createdAt: "2021-08-01T00:00:00.000Z",
    updatedAt: "2021-08-01T00:00:00.000Z",
  },
  {
    _id: "2",
    rollNo: "IT2021045",
    name: "Emma Watson",
    gender: "Female",
    email: "emma.w@student.college.edu",
    phone: "+1-555-0103",
    dob: "2003-08-22",
    dept: "2",
    batch: "2",
    year: "4th Year",
    semester: "8th",
    cgpa: 9.2,
    status: "active",
    address: "456 University Ave",
    guardianName: "Sarah Watson",
    guardianPhone: "+1-555-0104",
    internship: "Microsoft",
    placement: "placed",
    createdAt: "2021-08-01T00:00:00.000Z",
    updatedAt: "2021-08-01T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  students: StudentType[],
  filters: StudentParamsType
): { data: StudentType[]; total: number } => {
  let result = [...students];

  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.rollNo.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.dept.toLowerCase().includes(q)
    );
  }

  if (filters.status && filters.status !== "all") {
    result = result.filter((s) => s.status === filters.status);
  }

  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);

  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllStudents = async (filters: StudentParamsType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/students/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_STUDENTS, filters);
  return {
    students: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as StudentMetaType,
  };
};

export const fetchStudentById = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get(`/students/${_id}`);
  const student = DUMMY_STUDENTS.find((s) => s._id === _id);
  if (!student) throw new Error("Student not found");
  return student;
};

export const createStudent = async (data: StudentCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.post("/students/", data);
  const newStudent: StudentType = {
    ...data,
    _id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_STUDENTS.push(newStudent);
  return newStudent;
};

export const updateStudent = async (_id: string, data: StudentCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.put(`/students/${_id}`, data);
  const index = DUMMY_STUDENTS.findIndex((s) => s._id === _id);
  if (index === -1) throw new Error("Student not found");
  DUMMY_STUDENTS[index] = {
    ...DUMMY_STUDENTS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_STUDENTS[index];
};

export const deleteStudent = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.delete(`/students/${_id}`);
  const index = DUMMY_STUDENTS.findIndex((s) => s._id === _id);
  if (index === -1) throw new Error("Student not found");
  const [deleted] = DUMMY_STUDENTS.splice(index, 1);
  return deleted;
};

export const fetchStudentOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  await simulateDelay();
  const search = (params.search || "").toLowerCase();
  const filtered = DUMMY_STUDENTS.filter(
    (s) =>
      s.name.toLowerCase().includes(search) ||
      s.rollNo.toLowerCase().includes(search)
  );
  return {
    items: filtered.map((s) => ({
      label: `${s.name} (${s.rollNo})`,
      value: s._id,
    })),
    hasMore: false,
    total: filtered.length,
  };
};

export const createRealStudent = async (data: { batch_id: string, roll_number: string, name: string, org_email: string, personal_email: string, phone: string, cgpa: number }) => {
  const res = await axiosInstance.post("/v1/students", data);
  return res.data?.data || res.data;
};

export interface StudentEnrollmentType {
  id: string;
  student_id: string;
  college_name?: string;
  degree_type?: string;
  degree_name?: string;
  roll_number?: string;
  start_year?: number;
  end_year?: number | null;
  status?: string;
  is_alumni?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface BatchStudentDetailType {
  student_id: string;
  tenant_id?: string;
  roll_number?: string;
  is_open_to_work?: boolean;
  current_enrollment?: StudentEnrollmentType | null;
  last_degree?: StudentEnrollmentType | null;
  /** Full name comes from the list response but may not exist in detail — we store it optionally */
  full_name?: string;
}

export const fetchStudentDetailById = async (id: string): Promise<BatchStudentDetailType> => {
  const res = await axiosInstance.get(`/v1/students/${id}`);
  return res.data?.data || res.data;
};

export interface BatchStudentType {
  id: string;
  full_name: string;
  org_email: string;
  personal_email?: string;
  phone?: string;
  roll_number: string;
  year_of_study?: number;
  department?: string;
  graduation_year?: number;
  cgpa?: number;
  is_eligible?: boolean;
  is_open_to_work?: boolean;
  is_active: boolean;
  created_at?: string;
}

export type BatchStudentParamsType = {
  batch_id: string;
  eligible_only?: boolean;
  page?: number;
  limit?: number;
};

export const fetchStudentsByBatch = async (
  params: BatchStudentParamsType
) => {
  const res = await axiosInstance.get("/v1/students", { params });
  // Response shape: { success, data: { total, page, limit, items: [...] } }
  const inner = res.data?.data ?? res.data;
  const students: BatchStudentType[] = inner.items || inner.data || [];
  const total: number = inner.total ?? students.length;
  const page: number = inner.page ?? params.page ?? 1;
  const limit: number = inner.limit ?? params.limit ?? 20;
  return {
    students,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    },
  };
};

export interface StudentUpdateType {
  name?: string;
  phone?: string;
  cgpa?: number;
  current_semester?: number;
  current_year?: number;
}

export const updateStudentDetail = async (
  id: string,
  data: StudentUpdateType
): Promise<BatchStudentDetailType> => {
  const res = await axiosInstance.put(`/v1/students/${id}`, data);
  return res.data?.data || res.data;
};

export const deleteStudentDetail = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/v1/students/${id}`);
};

export type BulkUploadDocType = "csv" | "xlsx" | "google_sheet";

export interface BulkUploadParams {
  batch_id: string;
  doc_type: BulkUploadDocType;
  file?: File;
  sheet_url?: string;
}

export const bulkUploadStudents = async (params: BulkUploadParams) => {
  const form = new FormData();
  form.append("batch_id", params.batch_id);
  form.append("doc_type", params.doc_type);
  if (params.file) form.append("file", params.file);
  if (params.sheet_url) form.append("sheet_url", params.sheet_url);
  const res = await axiosInstance.post("/v1/students/bulk-upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data?.data || res.data;
};

export const studentService = {
  fetchAllStudents,
  fetchStudentById,
  createStudent,
  createRealStudent,
  updateStudent,
  deleteStudent,
  fetchStudentOptions,
  fetchStudentsByBatch,
  fetchStudentDetailById,
  updateStudentDetail,
  deleteStudentDetail,
};
